<template>
  <div class="paper-generation-container">
    <!-- 顶部导航：学段-年级-学科-教材选择 -->
    <div class="header-nav">
      <div class="subject-selector">
        <el-dropdown @command="handleStageChange">
          <span class="selector-item">
            {{ currentStageName }}
            <el-icon>
              <ArrowDown />
            </el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="item in stageList" :key="item.stageId" :command="item">
                {{ item.stageName }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <span class="separator">·</span>

        <!-- 年级筛选项 - 只在章节模式显示 -->
        <template v-if="activeTab === 'chapter'">
          <el-dropdown @command="handleGradeChange" :disabled="!currentStageId">
            <span class="selector-item">
              {{ currentGradeName }}
              <el-icon>
                <ArrowDown />
              </el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-for="item in gradeList" :key="item.gradeId" :command="item">
                  {{ item.gradeName }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <span class="separator">·</span>
        </template>

        <el-dropdown @command="handleSubjectChange"
          :disabled="activeTab === 'chapter' ? !currentGradeId : !currentStageId">
          <span class="selector-item">
            {{ currentSubjectName }}
            <el-icon>
              <ArrowDown />
            </el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item v-for="item in subjectList" :key="item.subjectId" :command="item">
                {{ item.subjectName }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <span class="separator">·</span>

        <!-- 教材筛选项 - 只在章节模式显示 -->
        <template v-if="activeTab === 'chapter'">
          <el-dropdown @command="handleTextBookChange" :disabled="!currentSubjectId">
            <span class="selector-item">
              {{ currentTextBookName }}
              <el-icon>
                <ArrowDown />
              </el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-for="item in filteredTextBookList" :key="item.textbookId" :command="item">
                  {{ item.textbookName }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </div>

      <div class="step-indicator">
        <span class="step active">01 选择章节</span>
        <span class="step-arrow">></span>
        <span class="step">02 组卷设置</span>
        <span class="step-arrow">></span>
        <span class="step">03 试题设置</span>
      </div>
    </div>

    <!-- 主内容区：三列布局 -->
    <div class="main-content">
      <!-- 左侧：教材目录树/知识点树 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">选择{{ activeTab === "chapter" ? "章节" : "知识点" }}（从左侧勾选）</span>
          <el-button type="primary" @click="clearSelectedNodes">清空</el-button>
        </div>

        <!-- 章节/知识点切换 -->
        <div class="tab-header">
          <el-radio-group v-model="activeTab" size="small" @change="handleTabChange">
            <el-radio-button label="chapter">章节</el-radio-button>
            <el-radio-button label="knowledge">知识点</el-radio-button>
          </el-radio-group>

          <!-- 加载状态按钮 - 知识点模式 -->
          <el-button v-if="
            activeTab === 'knowledge' && knowledgeList.length === 0 && !knowledgeLoading && canShowKnowledge
          " type="primary" link @click="loadKnowledgeData">
            加载知识点
          </el-button>
        </div>

        <!-- 已选标签区域 -->
        <div class="selected-tags" v-if="selectedNodes.length > 0">
          <el-tag v-for="node in selectedNodes" :key="node.id" closable size="small" type="success"
            @close="removeSelectedNode(node)">
            {{ node.label }}
          </el-tag>
        </div>

        <!-- 章节模式：缺省状态提示 - 未选择教材 -->
        <div v-if="activeTab === 'chapter' && !canShowChapter" class="empty-state">
          <el-empty description="请先选择教材" :image-size="120" />
        </div>

        <!-- 知识点模式：缺省状态提示 - 未选择学科 -->
        <div v-else-if="activeTab === 'knowledge' && !canShowKnowledge" class="empty-state">
          <el-empty description="请先选择学科" :image-size="120" />
        </div>

        <!-- 加载中状态 -->
        <div v-else-if="dataLoading" class="loading-state">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>加载中...</span>
        </div>

        <!-- 树形结构 - 章节模式 -->
        <div v-else-if="activeTab === 'chapter'" class="tree-container">
          <!-- 当有教材但无数据时显示提示 -->
          <div v-if="canShowChapter && (!treeData || treeData.length === 0) && !dataLoading" class="empty-state small">
            <el-empty description="暂无章节数据" :image-size="80" />
          </div>

          <el-tree v-else ref="treeRef" :data="treeData" show-checkbox node-key="id" :props="treeProps"
            :default-expanded-keys="defaultExpandedKeys" @check="handleTreeCheck" @node-expand="handleNodeExpand"
            @node-collapse="handleNodeCollapse" :filter-node-method="filterNode" class="custom-tree"
            :check-strictly="true">
            <template #default="{ node, data }">
              <span class="custom-tree-node" :class="{ 'has-children': data.children?.length > 0 }">
                <el-icon v-if="data.type === 'chapter'" class="chapter-icon">
                  <Folder />
                </el-icon>
                <el-icon v-else-if="data.type === 'section'" class="section-icon">
                  <Document />
                </el-icon>
                <span>{{ node.label }}</span>
                <span class="node-type" v-if="data.type === 'section'">(节)</span>
              </span>
            </template>
          </el-tree>
        </div>

        <!-- 树形结构 - 知识点模式 -->
        <div v-else-if="activeTab === 'knowledge'" class="tree-container">
          <!-- 当有学科但无数据时显示提示 -->
          <div v-if="canShowKnowledge && (!knowledgeTreeData || knowledgeTreeData.length === 0) && !knowledgeLoading"
            class="empty-state small">
            <el-empty description="暂无知识点数据" :image-size="80">
              <el-button type="primary" size="small" @click="loadKnowledgeData">重新加载</el-button>
            </el-empty>
          </div>

          <el-tree v-else ref="knowledgeTreeRef" :data="knowledgeTreeData" show-checkbox node-key="id"
            :props="knowledgeTreeProps" :default-expanded-keys="knowledgeExpandedKeys" @check="handleKnowledgeCheck"
            @node-expand="handleKnowledgeExpand" @node-collapse="handleKnowledgeCollapse"
            class="custom-tree knowledge-tree" :check-strictly="true">
            <template #default="{ node, data }">
              <span class="custom-tree-node">
                <el-icon class="knowledge-icon">
                  <Collection />
                </el-icon>
                <span>{{ node.label }}</span>
                <span class="knowledge-count" v-if="data.questionCount">({{ data.questionCount }})</span>
              </span>
            </template>
          </el-tree>
        </div>
      </div>

      <!-- 中间：组卷设置（保持不变） -->
      <div class="center-panel">
        <div class="panel-header">
          <span class="panel-title">02 组卷设置</span>
        </div>

        <el-form label-position="top" size="default">
          <!-- 难度设置 -->
          <el-form-item label="题目难度">
            <div class="difficulty-setting flex">
              <!-- <el-radio-group v-model="paperConfig.difficultyType">
                <el-radio value="comprehensive">整卷综合难度</el-radio>
                <el-radio value="separate">分题设置难度</el-radio>
              </el-radio-group>
              <el-select v-model="paperConfig.difficultyLevel" placeholder="选择难度" class="difficulty-select"
                v-if="paperConfig.difficultyType === 'comprehensive'">
                <el-option label="简单" value="easy" />
                <el-option label="适中" value="medium" />
                <el-option label="困难" value="hard" />
              </el-select> -->
              <el-select v-model="paperConfig.minDifficulty" placeholder="请选择最小难度" style="width: 200px">
                <el-option v-for="item in difficultOptions" :key="item.value" :label="item.label" :value="item.value">
                </el-option>
              </el-select>
              <el-select v-model="paperConfig.maxDifficulty" placeholder="请选择最大难度" style="width: 200px">
                <el-option v-for="item in difficultOptions" :key="item.value" :label="item.label" :value="item.value">
                </el-option>
              </el-select>
            </div>
          </el-form-item>

          <!-- 优先地区 -->
          <!-- <el-form-item label="优先地区">
            <el-select v-model="paperConfig.region" placeholder="全部" clearable>
              <el-option label="全部" value="" />
              <el-option label="北京" value="beijing" />
              <el-option label="上海" value="shanghai" />
              <el-option label="广东" value="guangdong" />
              <el-option label="江苏" value="jiangsu" />
              <el-option label="浙江" value="zhejiang" />
            </el-select>
          </el-form-item> -->
          <el-form-item label="优先地区">
            <div class="region-selector">
              <!-- 已选地区显示（当有值时） -->
              <div class="selected-region" v-if="paperConfig.region">
                <el-tag type="success" closable @close="clearRegion" size="default">
                  {{ paperConfig.region }}
                </el-tag>
              </div>

              <!-- 级联选择器 -->
              <el-cascader v-model="regionCascader" :options="cascaderOptions" :props="cascaderProps"
                placeholder="请选择省/市/区" clearable filterable @change="handleCascaderChange" style="width: 100%"
                :show-all-levels="false" />

              <!-- 热门城市快捷选择 -->
              <div class="hot-cities" v-if="hotCities.length > 0">
                <span class="hot-label">热门城市：</span>
                <el-tag v-for="city in hotCities.slice(0, 8)" :key="city.code" size="small"
                  :type="regionCascader.includes(city.code) ? 'success' : 'info'" @click="selectHotCity(city)"
                  class="hot-tag" effect="plain">
                  {{ city.name }}
                </el-tag>
              </div>
            </div>
          </el-form-item>

          <!-- 优先年份 -->
          <el-form-item label="优先年份">
            <el-date-picker v-model="paperConfig.year" type="year" placeholder="请选择年份" format="YYYY" value-format="YYYY"
              :clearable="true" style="width: 100%" />
          </el-form-item>

          <!-- 四轮复习 - 保持原有结构，但确保只存值 -->
          <el-form-item label="四轮">
            <el-checkbox-group v-model="paperConfig.fourRound">
              <template v-for="(item, index) in planOptions" :key="index">
                <el-checkbox :value="item.value">{{ item.label }}</el-checkbox>
              </template>
            </el-checkbox-group>
          </el-form-item>

          <!-- 七阶认知 - 保持原有结构，但确保只存值 -->
          <el-form-item label="七阶">
            <el-checkbox-group v-model="paperConfig.sevenPhase">
              <template v-for="(item, index) in phaseOptions" :key="index">
                <el-checkbox :value="item.value">{{ item.label }}</el-checkbox>
              </template>
            </el-checkbox-group>
          </el-form-item>
        </el-form>
      </div>

      <!-- 右侧：试题设置 -->
      <div class="right-panel">
        <div class="panel-header">
          <span class="panel-title">03 试题设置</span>
          <!-- 添加总题数提示 -->
          <div class="total-questions-tip" v-if="questionTypeList.length > 0">
            <span :class="{ 'warning': isMaxQuestionsReached }">
              当前总题数：{{ currentTotalQuestionCount }}/150
            </span>
          </div>

        </div>
        <div v-if="isMaxQuestionsReached" class="limit-warning">已达上限</div>

        <!-- 加载状态 -->
        <div v-if="questionTypeLoading" class="loading-state small">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>加载题型中...</span>
        </div>

        <!-- 题型列表 -->
        <div v-else-if="questionTypeList.length > 0" class="question-types hidden_scroll">
          <div v-for="item in questionTypeList" :key="item.questionTypeId" class="type-item"
            :class="{ 'has-count': item.questionTotalCount > 0 }">
            <div class="type-info">
              <span class="type-name">{{ item.questionTypeName }}</span>
              <span class="type-total" :class="{ zero: item.questionTotalCount == 0 }">
                题库共 {{ item.questionTotalCount }} 题
              </span>
            </div>

            <div class="type-config">
              <div class="count-config">
                <span class="config-label">数量</span>
                <el-input-number v-model="getTypeConfig(item.questionTypeId).count" :min="0"
                  :max="getTypeMaxLimit(item)" :disabled="item.questionTotalCount == 0" size="small"
                  controls-position="right" @change="handleTypeCountChange(item)" @input="handleTypeCountInput(item)" />
              </div>

              <div class="score-config">
                <span class="config-label">分值</span>
                <el-input-number v-model="getTypeConfig(item.questionTypeId).score" :min="0" :max="100" :precision="1"
                  :step="0.5" size="small" controls-position="right" placeholder="0" />
              </div>
            </div>
          </div>

          <!-- 总分统计 -->
          <div class="total-score-summary" v-if="paperConfig.questionType.length > 0">
            <el-divider />
            <div class="total-score">
              <span>试卷总分：</span>
              <span class="score-value">{{ totalScore }}</span>
            </div>
            <div class="total-questions">
              <span>总题数：</span>
              <span class="questions-value" :class="{ 'warning-text': isMaxQuestionsReached }">
                {{ currentTotalQuestionCount }}
              </span>
              <span class="total-limit">/ 150</span>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <el-empty v-else description="暂无题型数据" :image-size="100">
          <el-button type="primary" @click="getQuestionTypeList">重新加载</el-button>
        </el-empty>

        <!-- 配置汇总 -->
        <div class="config-summary" v-if="showSummary">
          <el-divider />
          <div class="summary-title">当前配置</div>
          <pre class="summary-content">{{ JSON.stringify(paperConfig, null, 2) }}</pre>
        </div>
      </div>
    </div>

    <!-- 底部操作按钮 -->
    <div class="footer-actions">
      <el-button @click="resetConfig">重置</el-button>
      <el-button type="primary" @click="generatePaper">生成试卷</el-button>
      <!-- <el-button type="success" @click="showSummary = !showSummary">
        {{ showSummary ? "隐藏" : "查看" }}配置
      </el-button> -->
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  ArrowDown,
  Loading,
  Folder,
  Document,
  Collection,
} from "@element-plus/icons-vue";
import areaData from "@/assets/data/area";

export default {
  name: "GjkcH5RandomPaper",

  components: {
    ArrowDown,
    Loading,
    Folder,
    Document,
    Collection,
  },

  computed: {
    ...mapState({
      originSubjectList: (state) => state.filter.subjectList,
      stageList: (state) => state.filter.stageList,
      allGradeList: (state) => state.filter.gradeList,
      allTextBookList: (state) => state.filter.textBookList || [],
    }),

    currentStageName() {
      return this.currentStage?.stageName || "选择学段";
    },

    currentGradeName() {
      return this.currentGrade?.gradeName || "选择年级";
    },

    currentSubjectName() {
      return this.currentSubject?.subjectName || "选择学科";
    },

    currentTextBookName() {
      return this.currentTextBook?.textbookName || "选择教材";
    },

    currentStageId() {
      return this.currentStage?.stageId;
    },

    currentGradeId() {
      return this.currentGrade?.gradeId;
    },

    currentSubjectId() {
      return this.currentSubject?.subjectId;
    },

    // 过滤后的教材列表
    filteredTextBookList() {
      let list = [...this.allTextBookList];

      // 按学段筛选
      if (this.currentStageId) {
        list = list.filter((item) => item.stageId == this.currentStageId);
      }

      // 按年级筛选
      if (this.currentGradeId) {
        list = list.filter((item) => item.gradeId == this.currentGradeId);
      }

      // 按学科筛选
      if (this.currentSubjectId) {
        list = list.filter((item) => item.subjectId == this.currentSubjectId);
      }

      return list;
    },

    // 第一个教材
    firstTextBook() {
      return this.filteredTextBookList.length > 0 ? this.filteredTextBookList[0] : null;
    },
    // 试卷总分
    totalScore() {
      if (!this.paperConfig.questionType || this.paperConfig.questionType.length === 0)
        return 0;
      return this.paperConfig.questionType.reduce((total, item) => {
        return total + item.count * item.score;
      }, 0);
    },

    // 总题数
    totalQuestionCount() {
      if (!this.paperConfig.questionType || this.paperConfig.questionType.length === 0)
        return 0;
      return this.paperConfig.questionType.reduce((total, item) => {
        return total + (item.count || 0);
      }, 0);
    },
    // 生成试卷名称
    generatedPaperName() {
      const parts = [];

      // 学段
      if (this.currentStage?.stageName) {
        parts.push(this.currentStage.stageName);
      }

      // 年级
      if (this.currentGrade?.gradeName) {
        parts.push(this.currentGrade.gradeName);
      }

      // 学科
      if (this.currentSubject?.subjectName) {
        parts.push(this.currentSubject.subjectName);
      }

      // 教材/学期（如果存在）
      if (this.currentTextBook?.textbookName) {
        parts.push(this.currentTextBook.textbookName);
      }

      // 如果没有选中任何内容，返回默认名称
      if (parts.length === 0) {
        return "随机组卷";
      }

      // 拼接名称
      return parts.join("") + "试卷";
    },
    // 当前总题数（用于限制）
    currentTotalQuestionCount() {
      if (!this.paperConfig.questionType || this.paperConfig.questionType.length === 0) return 0;
      return this.paperConfig.questionType.reduce((total, item) => {
        return total + (item.count || 0);
      }, 0);
    },

    // 是否已达到最大题数
    isMaxQuestionsReached() {
      return this.currentTotalQuestionCount >= 150;
    },

    // 剩余可加题数
    remainingQuestions() {
      return 150 - this.currentTotalQuestionCount;
    },
    // 是否有选中的教材
    hasSelectedTextBook() {
      return !!(this.currentTextBook?.textbookId);
    },
    // 是否有选中的学科（知识点模式需要）
    hasSelectedSubject() {
      return !!(this.currentSubject?.subjectId);
    },

    // 是否可以展示章节列表
    canShowChapter() {
      return this.hasSelectedTextBook;
    },

    // 是否可以展示知识点列表
    canShowKnowledge() {
      return this.hasSelectedSubject;
    },
  },

  data() {
    return {
      // 学段年级学科教材
      gradeList: [],
      subjectList: [],
      textBookList: [],
      currentStage: null,
      currentGrade: null,
      currentSubject: null,
      currentTextBook: null,
      isInitializing: false, // 标记是否正在初始化

      // 树形结构 - 章节
      activeTab: "chapter",
      treeData: [],
      selectedNodes: [],
      defaultExpandedKeys: [],
      treeProps: {
        label: "name",
        children: "children",
      },

      // 树形结构 - 知识点
      knowledgeList: [],
      knowledgeTreeData: [],
      knowledgeSelectedNodes: [],
      knowledgeExpandedKeys: [],
      knowledgeTreeProps: {
        label: "name",
        children: "children",
      },

      // 加载状态
      dataLoading: false,
      knowledgeLoading: false,

      // 试卷难度
      difficultOptions: [
        { label: "0.5星", value: 1 },
        { label: "1.0星", value: 2 },
        { label: "1.5星", value: 3 },
        { label: "2.0星", value: 4 },
        { label: "2.5星", value: 5 },
        { label: "3.0星", value: 6 },
        { label: "3.5星", value: 7 },
        { label: "4.0星", value: 8 },
        { label: "4.5星", value: 9 },
        { label: "5.0星", value: 10 },
        { label: "5.5星", value: 11 },
        { label: "6.0星", value: 12 },
        { label: "6.5星", value: 13 },
        { label: "7.0星", value: 14 },
      ],

      // 组卷配置
      paperConfig: {
        // 基础信息
        stageId: null,
        gradeId: null,
        subjectId: null,
        textbookId: null,

        // 难度设置
        difficultyType: "comprehensive",
        difficultyLevel: "medium",
        maxDifficulty: "",
        minDifficulty: "",

        // 筛选条件
        region: "",
        areaId: null, // 新增：选中的地区ID（省/市/区县的code）
        year: "",
        fourRound: [],
        sevenPhase: [],

        // 试题数量
        questionType: [],

        // 已选章节/知识点
        selectedNodes: [],
        // 已选章节/知识点 - 改为只存ID数组
        chapterId: [], // 选中的章节ID数组
        knowledgeId: [], // 选中的知识点ID数组
        loading: {},
        paperName: "", // 试卷名称
      },

      // 题型列表
      questionType: [
        { type: "modernReading", name: "现代文阅读" },
        { type: "languageUse", name: "语言文字运用" },
        { type: "classicalReading", name: "文言文阅读" },
        { type: "poetryReading", name: "古代诗词阅读" },
        { type: "dictation", name: "名篇名句默写" },
        { type: "composition", name: "作文题" },
        { type: "wholeBook", name: "整本书阅读" },
        { type: "judgment", name: "判断题" },
      ],

      // 显示控制
      showSummary: false,
      chapterData: [], // 存储原始章节数据
      // 地区选择相关 - 级联选择器
      regionCascader: [], // 级联选择器绑定值
      cascaderOptions: [], // 级联选择器选项
      cascaderProps: {
        value: "code",
        label: "name",
        children: "children",
        checkStrictly: true, // 可以选择任意一级
        emitPath: false, // 只输出选中项的值，不输出路径
        expandTrigger: "hover", // 悬停展开
      },
      hotCities: [], // 热门城市
      planOptions: [], // 四轮
      phaseOptions: [], // 七阶
      // 题型相关
      questionTypeList: [], // 题型列表
      questionTypeLoading: false, // 题型加载状态
      questionTypeMap: {}, // 题型映射（用于快速查找）
      childDictParams: {
        dictPid: "",
      }, // 字典参数
      // 教材选择状态
      needSelectTextBook: false, // 是否需要选择教材
    };
  },

  watch: {
    // 监听标签页切换
    activeTab: {
      handler(newVal, oldVal) {
        console.log(`标签页切换: ${oldVal} -> ${newVal}`);

        if (newVal === "chapter") {
          // 切换到章节模式
          // 如果有教材则加载章节数据
          if (this.currentTextBook?.textbookId) {
            this.getChapterAll(true);
          } else if (this.currentSubject?.subjectId) {
            // 如果有学科但没有教材，需要重新加载年级和教材列表
            this.loadChapterPrerequisites();
          } else {
            this.loadMockTreeData();
          }
        } else if (newVal === "knowledge") {
          // 切换到知识点模式
          // 如果有学科则加载知识点数据
          if (this.currentSubject?.subjectId) {
            this.getKnowledgeAll(true);
          } else {
            // 没有学科时显示空状态
            this.clearKnowledgeData();
          }
        }
      },
      immediate: true,
    },

    selectedNodes: {
      handler(newVal) {
        this.paperConfig.selectedNodes = newVal;
      },
      deep: true,
    },

    knowledgeSelectedNodes: {
      handler(newVal) {
        // 合并知识点选中的节点到 selectedNodes
        this.paperConfig.selectedNodes = [
          ...this.selectedNodes,
          ...newVal.map((n) => ({ ...n, source: "knowledge" })),
        ];
      },
      deep: true,
    },

    // 学段切换时清空数据
    currentStage: {
      handler(stage, oldStage) {
        this.paperConfig.stageId = stage?.stageId;

        // 当学段变化时（且不是初始化过程中）
        if (!this.isInitializing && oldStage && oldStage.stageId !== stage?.stageId) {
          // 清空下级选项
          this.currentGrade = null;
          this.currentSubject = null;
          this.currentTextBook = null;

          // 清空章节和知识点数据
          this.clearChapterAndKnowledgeData();
        }
      },
      deep: true,
    },

    // 年级切换时清空数据（知识点模式不需要教材，但学段变化会影响知识点）
    currentGrade: {
      handler(grade, oldGrade) {
        this.paperConfig.gradeId = grade?.gradeId;

        // 当年级变化时（且不是初始化过程中）
        if (!this.isInitializing && oldGrade && oldGrade.gradeId !== grade?.gradeId) {
          // 清空下级选项
          this.currentSubject = null;
          this.currentTextBook = null;

          // 清空章节数据（知识点数据也会受影响，因为学段/学科可能变化）
          this.clearChapterData();

          // 知识点数据：如果有学科，重新加载
          if (this.currentSubject?.subjectId) {
            this.getKnowledgeAll(true);
          } else {
            this.clearKnowledgeData();
          }
        }
      },
      deep: true,
    },

    // 学科切换时清空章节数据，但重新加载知识点
    currentSubject: {
      handler(subject, oldSubject) {
        this.paperConfig.subjectId = subject?.subjectId;

        // 当学科变化时（且不是初始化过程中）
        if (!this.isInitializing && oldSubject && oldSubject.subjectId !== subject?.subjectId) {
          // 清空教材选项
          this.currentTextBook = null;

          // 清空章节数据
          this.clearChapterData();

          // 重新加载知识点数据
          if (subject?.subjectId) {
            this.getKnowledgeAll(true);
          } else {
            this.clearKnowledgeData();
          }
        }
      },
      deep: true,
    },

    // 教材切换时只加载章节数据，不影响知识点
    currentTextBook: {
      handler(textBook, oldTextBook) {
        this.paperConfig.textbookId = textBook?.textbookId;

        // 教材变化时重新加载章节数据
        if (textBook && (!oldTextBook || oldTextBook.textbookId !== textBook.textbookId)) {
          if (this.activeTab === 'chapter') {
            this.getChapterAll(true);
          }
        } else if (!textBook) {
          // 没有选中教材时清空章节数据
          this.clearChapterData();
        }
      },
      deep: true,
    },

    // 监听教材列表变化，自动设置默认值
    filteredTextBookList: {
      handler(newList) {
        // 如果正在初始化或没有数据，直接返回
        if (this.isInitializing || newList.length === 0) return;

        // 检查是否已经有选中的值，如果没有才进行初始化
        if (
          !this.currentStage &&
          !this.currentGrade &&
          !this.currentSubject &&
          !this.currentTextBook
        ) {
          this.setDefaultValues();
        }
      },
      immediate: true,
      deep: true,
    },
  },

  mounted() {
    // 不需要在这里重复初始化
    this.initAreaData();
    // 轮次
    this.getRoundDict(2442);
    // 阶段
    this.getPhaseList(2412);
  },

  methods: {
    // 加载章节模式需要的前置数据（年级和教材列表）
    async loadChapterPrerequisites() {
      // 如果有学科，尝试获取该学科下的教材列表
      if (this.currentSubject?.subjectId) {
        // 重新过滤教材列表
        this.gradeList = this.allGradeList.filter(
          (item) => item.stageId == this.currentStage?.stageId
        );

        // 如果之前有选中的年级，尝试恢复
        if (this.currentGrade?.gradeId) {
          // 已有年级，重新获取教材列表
          this.filteredTextBookList(); // 重新计算过滤后的教材
        }
      }
    },
    // 清空章节和知识点数据
    // clearChapterAndKnowledgeData() {
    //   // 清空章节数据
    //   this.chapterData = [];
    //   this.treeData = [];
    //   this.defaultExpandedKeys = [];

    //   // 清空知识点数据
    //   this.knowledgeList = [];
    //   this.knowledgeTreeData = [];
    //   this.knowledgeExpandedKeys = [];

    //   // 清空选中的节点
    //   this.selectedNodes = [];
    //   this.knowledgeSelectedNodes = [];

    //   // 清空配置中的选中ID
    //   this.paperConfig.chapterId = [];
    //   this.paperConfig.knowledgeId = [];

    //   console.log("已清空章节和知识点数据");
    // },

    // 检查是否需要显示缺省状态
    shouldShowEmptyState() {
      if (this.activeTab === 'chapter') {
        return !this.hasSelectedTextBook;
      } else {
        return !this.hasSelectedTextBook;
      }
    },
    // 获取题型最大可选数量
    getTypeMaxLimit(item) {
      const typeInfo = this.questionTypeList.find(
        (t) => t.questionTypeId === item.questionTypeId
      );
      const maxByLibrary = Math.min(100, Number(typeInfo?.questionTotalCount || 100));
      const currentConfig = this.getTypeConfig(item.questionTypeId);
      const currentCount = currentConfig.count || 0;

      // 计算还能加多少题到150上限
      const remainingTotal = 150 - this.currentTotalQuestionCount + currentCount;

      // 返回两者中较小的值
      return Math.min(maxByLibrary, remainingTotal);
    },

    // 处理题型数量输入（实时限制）
    handleTypeCountInput(item) {
      const config = this.getTypeConfig(item.questionTypeId);
      const maxLimit = this.getTypeMaxLimit(item);

      if (config.count > maxLimit) {
        config.count = maxLimit;
        this.$message.warning(`该题型最多只能选择 ${maxLimit} 题，总题数不能超过150题`);
      }
    },

    // 获取题型配置（辅助方法）
    getTypeConfig(questionTypeId) {
      let config = this.paperConfig.questionType.find(
        (item) => item.questionTypeId === questionTypeId
      );

      if (!config) {
        // 如果不存在，创建一个默认配置
        const typeInfo = this.questionTypeList.find(
          (item) => item.questionTypeId === questionTypeId
        );
        config = {
          questionTypeId,
          questionTypeName: typeInfo?.questionTypeName || "",
          count: 0,
          score: 0,
        };
        this.paperConfig.questionType.push(config);
      }

      return config;
    },
    // 获取题型配置并带有限制逻辑
    getTypeConfigWithLimit(questionTypeId) {
      const config = this.getTypeConfig(questionTypeId);
      const typeInfo = this.questionTypeList.find(
        (item) => item.questionTypeId === questionTypeId
      );

      // 计算该题型当前能增加的最大数量
      const maxAllowed = Math.min(
        typeInfo?.questionTotalCount || 100,
        this.remainingQuestions + config.count
      );

      // 返回一个响应式对象，包含限制信息
      return {
        ...config,
        maxAllowed,
        remaining: this.remainingQuestions,
        isMaxReached: this.isMaxQuestionsReached
      };
    },

    // 初始化题型配置
    initQuestionTypes() {
      console.log("初始化题型配置，当前题型列表:", this.questionTypeList);

      // 清空原有配置
      this.paperConfig.questionType = [];

      // 为每个题型创建配置
      if (this.questionTypeList && this.questionTypeList.length > 0) {
        this.questionTypeList.forEach((item) => {
          this.paperConfig.questionType.push({
            questionTypeId: item.questionTypeId,
            questionTypeName: item.questionTypeName,
            count: 0,
            score: 0,
          });
        });
      }

      console.log("初始化后的 questionType:", this.paperConfig.questionType);
    },

    // 处理题型数量变化
    handleTypeCountChange(item) {
      const config = this.getTypeConfig(item.questionTypeId);
      // 可以在这里添加额外的逻辑，比如自动计算分值等
      console.log(`题型 ${item.questionTypeName} 数量变为:`, config.count);
    },

    // 展开所有题型（设置默认数量）
    expandAllTypes() {
      this.questionTypeList.forEach((item) => {
        if (item.questionTotalCount > 0) {
          const config = this.getTypeConfig(item.questionTypeId);
          config.count = Math.min(5, Number(item.questionTotalCount));
        }
      });
    },

    // 清空所有题型设置
    clearAllTypes() {
      this.paperConfig.questionType.forEach((item) => {
        item.count = 0;
        item.score = 0;
      });
    },

    // 获取题型列表 - 修改
    async getQuestionTypeList() {
      // 使用最新的 subjectId，直接从 currentSubject 获取
      const subjectId = this.currentSubject?.subjectId || this.paperConfig.subjectId;

      if (!subjectId) {
        console.log("没有学科ID，不获取题型数据");
        this.questionTypeList = [];
        this.paperConfig.questionType = [];
        return;
      }

      this.questionTypeLoading = true;
      console.log("开始获取题型数据，学科ID:", subjectId);

      try {
        // 添加时间戳避免缓存
        const res = await this.$Api.examination.getQuestionTypeList(subjectId);
        console.log("题型数据响应:", res);

        const data = res.data?.data || [];
        console.log("处理后的题型数据:", data);

        // 更新数据
        this.questionTypeList = data;

        // 构建题型映射
        const map = {};
        data.forEach((item) => {
          map[item.questionTypeId] = item;
        });
        this.questionTypeMap = map;

        // 初始化题型配置
        this.initQuestionTypes();
      } catch (error) {
        console.error("获取题型数据失败:", error);
        this.questionTypeList = [];
        this.paperConfig.questionType = [];
      } finally {
        this.questionTypeLoading = false;
      }
    },

    getRoundDict(dictPid) {
      this.childDictParams.dictPid = dictPid;
      this.$Api.common
        .getChildsDict(this.childDictParams)
        .then((res) => {
          this.planOptions = [];
          res.data.data.forEach((item) => {
            this.planOptions.push({ label: item.dictName, value: item.dictId });
          });
          // for (var key in res.data.data) {
          //   this.planOptions.push({ label: res.data.data[key], value: key });
          // }
        })
        .catch(() => { });
    },
    getPhaseList(dictPid) {
      this.childDictParams.dictPid = dictPid;
      this.$Api.common
        .getChildsDict(this.childDictParams)
        .then((res) => {
          this.phaseOptions = [];
          res.data.data.forEach((item) => {
            this.phaseOptions.push({ label: item.dictName, value: item.dictId });
          });
          // for (var key in res.data.data) {
          //   this.phaseOptions.push({ label: res.data.data[key], value: key });
          // }
        })
        .catch(() => { });
    },
    // 初始化地区数据
    initAreaData() {
      this.buildCascaderOptions();
      this.initHotCities();
      console.log("地区数据初始化完成");
    },

    // 构建级联选择器数据
    buildCascaderOptions() {
      const options = [];

      // 遍历省份
      Object.entries(areaData.province_list).forEach(([provinceCode, provinceName]) => {
        const provinceItem = {
          code: provinceCode,
          name: provinceName,
          children: [],
        };

        // 查找该省份下的城市
        Object.entries(areaData.city_list).forEach(([cityCode, cityName]) => {
          if (cityCode.startsWith(provinceCode.substring(0, 2))) {
            const cityItem = {
              code: cityCode,
              name: cityName,
              children: [],
            };

            // 查找该城市下的区县
            Object.entries(areaData.county_list).forEach(([countyCode, countyName]) => {
              if (countyCode.startsWith(cityCode.substring(0, 4))) {
                cityItem.children.push({
                  code: countyCode,
                  name: countyName,
                });
              }
            });

            // 如果城市没有区县，就不显示children
            if (cityItem.children.length === 0) {
              delete cityItem.children;
            }

            provinceItem.children.push(cityItem);
          }
        });

        options.push(provinceItem);
      });

      this.cascaderOptions = options;
    },

    // 初始化热门城市
    initHotCities() {
      this.hotCities = Object.entries(areaData.hot_city_list).map(([code, name]) => ({
        code,
        name,
      }));
    },

    // 处理级联选择器变化
    handleCascaderChange(value) {
      console.log("级联选择器变化:", value);

      if (!value) {
        this.paperConfig.region = "";
        this.paperConfig.areaId = null; // 清空地区ID
        return;
      }

      // 根据选中值查找对应的完整名称路径
      const findFullPath = (code) => {
        // 尝试在省份中查找
        if (areaData.province_list[code]) {
          return areaData.province_list[code];
        }
        // 尝试在城市中查找
        if (areaData.city_list[code]) {
          return areaData.city_list[code];
        }
        // 尝试在区县中查找
        if (areaData.county_list[code]) {
          return areaData.county_list[code];
        }
        return "";
      };

      this.paperConfig.region = findFullPath(value);
      this.paperConfig.areaId = value; // 存储选中的地区ID（可能是省/市/区县的code）

      console.log("选中地区:", this.paperConfig.region);
      console.log("选中地区ID:", this.paperConfig.areaId);
    },

    // 选择热门城市
    selectHotCity(city) {
      console.log("选择热门城市:", city);

      // 热门城市通常是市级，找到对应的省级
      const provinceCode = city.code.substring(0, 2) + "0000";

      // 设置级联选择器的值
      this.regionCascader = [provinceCode, city.code];

      // 设置地区名称
      this.paperConfig.region = city.name;
      this.paperConfig.areaId = city.code; // 存储城市ID
    },

    // 清空地区选择
    clearRegion() {
      this.regionCascader = [];
      this.paperConfig.region = "";
      this.paperConfig.areaId = null; // 清空地区ID
    },

    // 设置默认值：根据第一个教材自动选择学段、年级、学科
    async setDefaultValues() {
      if (this.filteredTextBookList.length === 0 || this.isInitializing) return;

      this.isInitializing = true;
      console.log("开始初始化设置默认值");

      const firstBook = this.filteredTextBookList[0];
      if (!firstBook) {
        this.isInitializing = false;
        return;
      }

      console.log("设置默认值，第一个教材:", firstBook);

      try {
        // 1. 设置学段
        const stage = this.stageList.find((s) => s.stageId == firstBook.stageId);
        if (!stage) {
          this.isInitializing = false;
          return;
        }
        this.currentStage = stage;

        // 2. 设置年级列表和年级
        this.gradeList = this.allGradeList.filter(
          (item) => item.stageId == stage.stageId
        );
        const grade = this.gradeList.find((g) => g.gradeId == firstBook.gradeId);
        if (!grade) {
          this.isInitializing = false;
          return;
        }
        this.currentGrade = grade;

        // 3. 获取学科列表
        await this.getSubjectList(stage.stageId);

        // 4. 设置学科
        const subject = this.subjectList.find((s) => s.subjectId == firstBook.subjectId);
        if (!subject) {
          this.isInitializing = false;
          return;
        }
        this.currentSubject = subject;

        setTimeout(() => {
          // 获取题型列表
          this.getQuestionTypeList();
        }, 500);

        // 5. 最后设置教材
        this.currentTextBook = firstBook;

        console.log("默认值设置完成，教材ID:", firstBook.textbookId);

        // 根据当前激活的标签页加载数据
        if (this.activeTab === "chapter") {
          await this.getChapterAll();
        } else {
          await this.getKnowledgeAll();
        }
      } catch (error) {
        console.error("初始化失败:", error);
      } finally {
        // 延迟一点结束初始化状态，确保所有更新完成
        setTimeout(() => {
          this.isInitializing = false;
        }, 100);
      }
    },


    // 切换标签页时同步更新学科的下拉选项
    handleTabChange(tab) {
      if (tab === "knowledge" && this.knowledgeList.length === 0 && this.currentSubject?.subjectId) {
        this.loadKnowledgeData();
      } else if (tab === "chapter" && this.currentTextBook?.textbookId) {
        this.getChapterAll(true);
      }
    },

    // 学段切换
    handleStageChange(stage) {
      this.currentStage = stage;
      this.currentGrade = null;
      this.currentSubject = null;
      this.currentTextBook = null;

      // 过滤当前学段下的年级
      if (stage?.stageId) {
        this.gradeList = this.allGradeList.filter(
          (item) => item.stageId == stage.stageId
        );
      } else {
        this.gradeList = [];
      }

      // 清空章节和知识点数据
      this.clearChapterAndKnowledgeData();

      // 清空题型数据
      this.questionTypeList = [];
      this.paperConfig.questionType = [];
    },

    // 年级切换
    handleGradeChange(grade) {
      this.currentGrade = grade;
      this.currentSubject = null;
      this.currentTextBook = null;

      // 获取学科列表
      this.getSubjectList(grade?.stageId);

      // 清空章节数据
      this.clearChapterData();
    },

    // 获取学科列表
    async getSubjectList(stageId) {
      if (!stageId) return;

      try {
        const res = await this.$Api.common.getSubject({ stageId: stageId });
        this.subjectList = res.data.data || [];
        return this.subjectList;
      } catch {
        this.subjectList = [];
        return [];
      }
    },
    // 学科切换
    handleSubjectChange(subject) {
      console.log("学科切换:", subject);

      this.currentSubject = subject;
      this.currentTextBook = null;

      // 先更新 paperConfig.subjectId
      this.paperConfig.subjectId = subject?.subjectId;

      // 清空旧数据
      this.questionTypeList = [];
      this.paperConfig.questionType = [];

      // 根据当前模式决定后续操作
      if (this.activeTab === "chapter") {
        // 章节模式：清空章节数据，等待用户选择教材
        this.clearChapterData();
        // 清空教材选项
        this.currentTextBook = null;
        // 重新获取年级列表（如果有学段）
        if (this.currentStage?.stageId) {
          this.gradeList = this.allGradeList.filter(
            (item) => item.stageId == this.currentStage.stageId
          );
        }
      } else if (this.activeTab === "knowledge") {
        // 知识点模式：直接加载知识点数据
        this.clearKnowledgeData();
        this.$nextTick(() => {
          if (subject?.subjectId) {
            this.getKnowledgeAll(true);
          }
        });
      }

      // 获取题型列表（两种模式都需要）
      this.$nextTick(() => {
        if (subject?.subjectId) {
          this.getQuestionTypeList();
        }
      });
    },

    // 教材切换
    handleTextBookChange(textBook) {
      this.currentTextBook = textBook;
      this.paperConfig.textbookId = textBook?.textbookId;

      this.$nextTick(() => {
        if (this.activeTab === "chapter") {
          this.getChapterAll(true); // 强制刷新
        } else {
          this.getKnowledgeAll(true); // 强制刷新
        }
      });
    },

    // 获取章节数据
    async getChapterAll(forceRefresh = false) {
      if (!this.paperConfig.textbookId) {
        console.log("没有教材ID，不获取章节数据");
        return;
      }

      // 如果有缓存数据且不是强制刷新，直接使用缓存
      if (!forceRefresh && this.chapterData && this.chapterData.length > 0) {
        console.log("使用缓存的章节数据");
        const treeData = this.buildChapterTree(this.chapterData);
        this.treeData = this.sortChapterTree(treeData);
        this.defaultExpandedKeys = this.getAllNodeIds(treeData);
        return;
      }

      this.dataLoading = true;
      let params = {
        textbookId: this.paperConfig.textbookId,
      };
      console.log("获取章节数据，参数:", params);

      try {
        const res = await this.$Api.examination.getChapterAll(params);
        console.log("章节数据:", res.data.data);

        // 存储原始数据
        this.chapterData = res.data.data || [];

        // 构建树形数据并展示
        const treeData = this.buildChapterTree(this.chapterData);
        this.treeData = this.sortChapterTree(treeData);
        // 展开所有节点
        this.defaultExpandedKeys = this.getAllNodeIds(treeData);
        console.log("树形数据已更新:", this.treeData);
      } catch (error) {
        console.error("获取章节数据失败:", error);
        this.chapterData = [];
        // 失败时加载模拟数据
        this.loadMockTreeData();
      } finally {
        this.dataLoading = false;
      }
    },

    // 获取知识点列表 - 移除 textbookId 依赖
    async getKnowledgeAll(forceRefresh = false) {
      // 只需要学科ID即可获取知识点
      if (!this.paperConfig.subjectId) {
        console.log("没有学科ID，不获取知识点数据");
        this.clearKnowledgeData();
        return;
      }

      // 如果有缓存数据且不是强制刷新，直接使用缓存
      if (!forceRefresh && this.knowledgeList && this.knowledgeList.length > 0) {
        console.log("使用缓存的知识点数据");
        this.knowledgeTreeData = this.buildKnowledgeTree(this.knowledgeList);
        this.knowledgeExpandedKeys = this.getAllNodeIds(this.knowledgeTreeData);
        return;
      }

      this.knowledgeLoading = true;
      this.dataLoading = true;

      let params = {
        subjectId: this.paperConfig.subjectId,
        stageId: this.paperConfig.stageId,
        // 移除 textbookId
      };

      console.log("获取知识点数据，参数:", params);

      try {
        const res = await this.$Api.examination.getKnowledgeAll(params);
        console.log("知识点数据:", res.data.data);

        // 处理知识点数据，构建树形结构
        this.knowledgeList = res.data.data || [];
        this.knowledgeTreeData = this.buildKnowledgeTree(this.knowledgeList);
        this.knowledgeExpandedKeys = this.getAllNodeIds(this.knowledgeTreeData);
      } catch (error) {
        console.error("获取知识点数据失败:", error);
        this.knowledgeList = [];
        this.knowledgeTreeData = [];
      } finally {
        this.knowledgeLoading = false;
        this.dataLoading = false;
      }
    },

    // 清空知识点数据（单独方法）
    clearKnowledgeData() {
      this.knowledgeList = [];
      this.knowledgeTreeData = [];
      this.knowledgeExpandedKeys = [];
      this.knowledgeSelectedNodes = [];
      this.paperConfig.knowledgeId = [];
      console.log("已清空知识点数据");
    },

    // 清空章节数据（单独方法）
    clearChapterData() {
      this.chapterData = [];
      this.treeData = [];
      this.defaultExpandedKeys = [];
      this.selectedNodes = [];
      this.paperConfig.chapterId = [];
      console.log("已清空章节数据");
    },

    // 清空所有章节和知识点数据
    clearChapterAndKnowledgeData() {
      this.clearChapterData();
      this.clearKnowledgeData();
    },

    // 加载知识点数据（供按钮调用）
    loadKnowledgeData() {
      if (this.currentSubject?.subjectId) {
        this.getKnowledgeAll(true); // 强制刷新
      } else {
        this.$message.warning("请先选择学科");
      }
    },

    // 获取所有节点ID用于展开
    getAllNodeIds(nodes) {
      let ids = [];
      nodes.forEach((node) => {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
          ids = ids.concat(this.getAllNodeIds(node.children));
        }
      });
      return ids;
    },

    // 按sort字段排序
    sortChapterTree(treeData) {
      treeData.sort((a, b) => (a.sort || 0) - (b.sort || 0));

      treeData.forEach((node) => {
        if (node.children && node.children.length > 0) {
          this.sortChapterTree(node.children);
        }
      });

      return treeData;
    },

    // 将章节数据转换为树形结构
    buildChapterTree(chapterList) {
      if (!chapterList || chapterList.length === 0) return [];

      const nodeMap = new Map();

      // 首先将所有节点存入Map
      chapterList.forEach((chapter) => {
        nodeMap.set(chapter.chapterId, {
          id: chapter.chapterId,
          name: chapter.chapterName,
          type: chapter.chapterType === "1" ? "chapter" : "section",
          parentId: chapter.parentId,
          textbookId: chapter.textbookId,
          sort: chapter.sort || 0,
          children: [],
        });
      });

      // 构建树形结构
      const treeData = [];

      chapterList.forEach((chapter) => {
        const node = nodeMap.get(chapter.chapterId);

        if (chapter.parentId === "0") {
          treeData.push(node);
        } else {
          const parentNode = nodeMap.get(chapter.parentId);
          if (parentNode) {
            parentNode.children.push(node);
          } else {
            treeData.push(node);
          }
        }
      });

      return treeData;
    },

    // 构建知识点树形结构（正确版本）
    buildKnowledgeTree(knowledgeList) {
      if (!knowledgeList || knowledgeList.length === 0) return [];

      const nodeMap = new Map();

      // 1. 先将所有节点存入 Map
      knowledgeList.forEach((item) => {
        nodeMap.set(item.knowledgeId, {
          id: item.knowledgeId,
          name: item.knowledgeName,
          type: "knowledge",
          parentId: item.parentId,
          questionCount: item.questionCount || 0,
          children: [],
        });
      });

      // 2. 构建树形结构
      const treeData = [];

      knowledgeList.forEach((item) => {
        const node = nodeMap.get(item.knowledgeId);

        // 如果 parentId 为 0 或 null，或者找不到父节点，则作为根节点
        if (!item.parentId || item.parentId === "0" || !nodeMap.has(item.parentId)) {
          treeData.push(node);
        } else {
          const parentNode = nodeMap.get(item.parentId);
          if (parentNode) {
            parentNode.children.push(node);
          } else {
            // 找不到父节点时，也作为根节点
            treeData.push(node);
          }
        }
      });

      // 3. 按 sort 字段排序（如果有的话）
      this.sortKnowledgeTree(treeData);

      return treeData;
    },

    // 添加排序方法（可选）
    sortKnowledgeTree(treeData) {
      treeData.sort((a, b) => (a.sort || 0) - (b.sort || 0));

      treeData.forEach((node) => {
        if (node.children && node.children.length > 0) {
          this.sortKnowledgeTree(node.children);
        }
      });

      return treeData;
    },

    // 节点过滤方法
    filterNode(value, data) {
      if (!value) return true;
      return data.name.indexOf(value) > -1;
    },

    // 处理章节树节点勾选
    handleTreeCheck(data, { checkedNodes, checkedKeys }) {
      console.log("章节树勾选变化:", checkedNodes);
      console.log("章节树勾选keys:", checkedKeys);

      // 存储选中的节点信息用于显示标签
      this.selectedNodes = checkedNodes.map((node) => ({
        id: node.id,
        label: node.name,
        type: node.type,
        source: "chapter",
      }));

      // 存储章节ID数组
      this.paperConfig.chapterId = checkedKeys;
      console.log("选中的章节ID:", this.paperConfig.chapterId);
    },

    // 处理知识点树节点勾选
    handleKnowledgeCheck(data, { checkedNodes, checkedKeys }) {
      console.log("知识点树勾选变化:", checkedNodes);
      console.log("知识点树勾选keys:", checkedKeys);

      // 存储选中的节点信息用于显示标签
      this.knowledgeSelectedNodes = checkedNodes.map((node) => ({
        id: node.id,
        label: node.name,
        type: "knowledge",
        source: "knowledge",
      }));

      // 存储知识点ID数组
      this.paperConfig.knowledgeId = checkedKeys;
      console.log("选中的知识点ID:", this.paperConfig.knowledgeId);
    },

    // 移除已选节点
    removeSelectedNode(node) {
      console.log("移除节点:", node);

      if (node.source === "chapter") {
        // 章节节点
        if (this.$refs.treeRef) {
          // 取消树节点的勾选
          this.$refs.treeRef.setChecked(node.id, false);

          // 更新 chapterId 数组
          this.paperConfig.chapterId = this.paperConfig.chapterId.filter(
            (id) => id !== node.id
          );

          // 手动从 selectedNodes 中移除
          this.selectedNodes = this.selectedNodes.filter((n) => n.id !== node.id);
        }
      } else if (node.source === "knowledge") {
        // 知识点节点
        if (this.$refs.knowledgeTreeRef) {
          // 取消树节点的勾选
          this.$refs.knowledgeTreeRef.setChecked(node.id, false);

          // 更新 knowledgeId 数组
          this.paperConfig.knowledgeId = this.paperConfig.knowledgeId.filter(
            (id) => id !== node.id
          );

          // 手动从 knowledgeSelectedNodes 中移除
          this.knowledgeSelectedNodes = this.knowledgeSelectedNodes.filter(
            (n) => n.id !== node.id
          );
        }
      }
    },

    // 清空所有已选节点
    clearSelectedNodes() {
      if (this.$refs.treeRef) {
        this.$refs.treeRef.setCheckedKeys([]);
        this.paperConfig.chapterId = []; // 清空章节ID
      }
      if (this.$refs.knowledgeTreeRef) {
        this.$refs.knowledgeTreeRef.setCheckedKeys([]);
        this.paperConfig.knowledgeId = []; // 清空知识点ID
      }
      this.selectedNodes = [];
      this.knowledgeSelectedNodes = [];
    },

    // 节点展开/折叠
    handleNodeExpand(data) {
      console.log("展开:", data);
    },

    handleNodeCollapse(data) {
      console.log("折叠:", data);
    },

    handleKnowledgeExpand(data) {
      console.log("知识点展开:", data);
    },

    handleKnowledgeCollapse(data) {
      console.log("知识点折叠:", data);
    },

    // 加载树形数据（简化版本，主要用于章节）
    loadTreeData() {
      if (this.currentTextBook?.textbookId) {
        this.getChapterAll(); // 不强制刷新，使用缓存逻辑
      } else {
        this.loadMockTreeData();
      }
    },

    // 模拟树形数据
    loadMockTreeData() {
      if (this.activeTab === "chapter") {
        this.treeData = [
          {
            id: 1,
            name: "人教A版",
            type: "edition",
            children: [
              {
                id: 2,
                name: "必修第一册",
                type: "book",
                children: [
                  {
                    id: 3,
                    name: "第一章 集合与常用逻辑用语",
                    type: "chapter",
                    children: [
                      { id: 4, name: "1.1 集合的概念", type: "section" },
                      {
                        id: 5,
                        name: "1.2 集合的基本关系",
                        type: "section",
                        children: [
                          { id: 6, name: "1.2.1 充分条件与必要条件", type: "subsection" },
                          { id: 7, name: "1.2.2 充要条件", type: "subsection" },
                        ],
                      },
                      { id: 8, name: "1.3 集合的基本运算", type: "section" },
                      { id: 9, name: "1.4 充分条件与必要条件", type: "section" },
                    ],
                  },
                ],
              },
              { id: 10, name: "必修第二册", type: "book" },
              { id: 11, name: "选择性必修第一册", type: "book" },
              { id: 12, name: "选择性必修第二册", type: "book" },
              { id: 13, name: "选择性必修第三册", type: "book" },
            ],
          },
        ];
      } else {
        this.treeData = [
          {
            id: 101,
            name: "函数与导数",
            type: "knowledge",
            children: [
              { id: 102, name: "函数的概念", type: "knowledge", questionCount: 12 },
              { id: 103, name: "函数的性质", type: "knowledge", questionCount: 8 },
              { id: 104, name: "基本初等函数", type: "knowledge", questionCount: 15 },
              { id: 105, name: "导数的概念", type: "knowledge", questionCount: 6 },
            ],
          },
          {
            id: 106,
            name: "三角函数",
            type: "knowledge",
            children: [
              { id: 107, name: "任意角的三角函数", type: "knowledge", questionCount: 10 },
              {
                id: 108,
                name: "三角函数的图像与性质",
                type: "knowledge",
                questionCount: 14,
              },
              { id: 109, name: "三角恒等变换", type: "knowledge", questionCount: 9 },
            ],
          },
        ];
      }

      this.defaultExpandedKeys = this.getAllNodeIds(this.treeData);
    },

    // 重置配置
    resetConfig() {
      this.paperConfig.difficultyType = "comprehensive";
      this.paperConfig.difficultyLevel = "medium";
      this.paperConfig.region = "";
      this.paperConfig.areaId = null; // 重置地区ID
      this.paperConfig.year = "";
      this.paperConfig.fourRound = []; // 重置为数组
      this.paperConfig.sevenPhase = []; // 重置为数组

      // 重置题型配置，但保留题型结构
      if (this.questionTypeList.length > 0) {
        this.initQuestionTypes();
      } else {
        this.paperConfig.questionType = [];
      }

      // 清空章节和知识点ID
      this.paperConfig.chapterId = [];
      this.paperConfig.knowledgeId = [];

      this.clearSelectedNodes();
      this.$message.success("配置已重置");
    },

    // 生成试卷
    generatePaper() {
      // 验证必填项
      if (!this.currentStage) {
        this.$message.warning("请选择学段");
        return;
      }
      if (!this.currentGrade) {
        this.$message.warning("请选择年级");
        return;
      }
      if (!this.currentSubject) {
        this.$message.warning("请选择学科");
        return;
      }
      if (!this.currentTextBook) {
        this.$message.warning("请选择教材");
        return;
      }

      // 验证章节或知识点至少选择一项
      // const hasSelected = this.paperConfig.chapterId?.length > 0 || this.paperConfig.knowledgeId?.length > 0;
      // if (!hasSelected) {
      //   this.$message.warning("请至少选择一个章节或知识点");
      //   return;
      // }

      // 验证题型数量
      const hasQuestionCount = this.paperConfig.questionType.some(
        (item) => item.count > 0
      );
      if (!hasQuestionCount) {
        this.$message.warning("请至少设置一种题型的数量");
        return;
      }

      // 验证分值设置
      // const hasZeroScore = this.paperConfig.questionType.some(item => item.count > 0 && item.score <= 0);
      // if (hasZeroScore) {
      //   this.$message.warning("请为已设置数量的题型填写分值");
      //   return;
      // }

      // 自动生成试卷名称
      this.paperConfig.paperName = this.generatedPaperName;

      // 生成最终配置
      const finalConfig = {
        ...this.paperConfig,
        stageName: this.currentStage?.stageName,
        gradeName: this.currentGrade?.gradeName,
        subjectName: this.currentSubject?.subjectName,
        textbookName: this.currentTextBook?.textbookName,
        totalScore: this.totalScore,
        totalQuestionCount: this.totalQuestionCount,
        // 确保这些字段是数组格式
        fourRound: this.paperConfig.fourRound || [],
        sevenPhase: this.paperConfig.sevenPhase || [],
        chapterId: this.paperConfig.chapterId || [],
        knowledgeId: this.paperConfig.knowledgeId || [],
        areaId: this.paperConfig.areaId, // 确保地区ID被传递
      };

      console.log("生成的试卷配置:", finalConfig);

      this.loading = this.$Dialog.loading("正在为你生成试卷...");
      // 调用生成试卷接口
      this.$Api.examination
        .generateRandomPaper(finalConfig)
        .then((res) => {
          this.loading.close();
          this.$message.success("试卷生成成功！");
          setTimeout(() => {
            this.href("/examPaperView", {
              paperDocumentId: res.data.data.buyerPaperDocumentId,
              isRandom: true,
            });
          }, 1000);
        })
        .catch(() => {
          this.loading.close();
          // this.$message.error("试卷生成失败！");
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.paper-generation-container {
  width: $pageMainWidth;
  margin: 0 auto;
  background: #f5f7fa;
  // min-height: 100vh;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
    sans-serif;
}

/* 顶部导航 */
.header-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.subject-selector {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;

  .selector-item {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    cursor: pointer;

    .el-icon {
      margin-left: 4px;
      font-size: 14px;
    }
  }

  .separator {
    margin: 0 10px;
    color: #ddd;
  }
}

.step-indicator {
  display: flex;
  align-items: center;

  .step {
    font-size: 14px;
    color: #999;
    margin: 0 5px;

    &.active {
      color: #13d6aa;
      font-weight: 500;
    }
  }

  .step-arrow {
    color: #ddd;
    margin: 0 5px;
  }
}

/* 主内容区 */
.main-content {
  display: flex;
  gap: 20px;
}

/* 公共面板样式 */
.left-panel,
.center-panel,
.right-panel {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
}

.left-panel {
  flex: 2;
}

.center-panel {
  flex: 1.5;
}

.right-panel {
  flex: 1;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;

  .panel-title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
  }
}

/* 标签页 */
.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

/* 已选标签 */
.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  max-height: 80px;
  overflow-y: auto;
  padding: 5px;
  background: #f9f9f9;
  border-radius: 4px;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #909399;

  .el-icon {
    font-size: 24px;
    margin-bottom: 10px;
    color: #13d6aa;
  }
}

/* 树形容器 */
.tree-container {
  max-height: 480px;
  overflow-y: auto;

  :deep(.el-tree) {
    background: transparent;

    .el-tree-node__content {
      height: 36px;
      transition: background-color 0.3s;

      &:hover {
        background-color: #f5f7fa;
      }
    }
  }

  .custom-tree-node {
    display: flex;
    align-items: center;

    .chapter-icon,
    .section-icon,
    .knowledge-icon {
      margin-right: 6px;
      font-size: 16px;
    }

    .chapter-icon {
      color: #e6a23c;
    }

    .section-icon {
      color: #67c23a;
    }

    .knowledge-icon {
      color: #13d6aa;
    }

    .node-type {
      margin-left: 4px;
      font-size: 12px;
      color: #999;
    }

    .knowledge-count {
      margin-left: 6px;
      font-size: 12px;
      color: #909399;
      background: #f4f4f5;
      padding: 2px 6px;
      border-radius: 10px;
    }
  }

  .knowledge-tree {
    :deep(.el-tree-node__content) {
      .el-tree-node__expand-icon {
        color: #13d6aa;
      }
    }
  }
}

/* 组卷设置 */
.difficulty-setting {
  display: flex;
  // flex-direction: column;
  gap: 10px;

  .difficulty-select {
    width: 100%;
  }
}

/* 地区选择器样式 */
.region-selector {
  .selected-region {
    margin-bottom: 10px;

    .el-tag {
      margin-right: 10px;
    }
  }

  .hot-cities {
    margin-top: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .hot-label {
      font-size: 12px;
      color: #909399;
      white-space: nowrap;
    }

    .hot-tag {
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      &.el-tag--success {
        background-color: #f0f9eb;
        border-color: #e1f3d8;
        color: #67c23a;
      }
    }
  }
}

/* 级联选择器下拉面板样式 */
:deep(.el-cascader-node) {
  .el-cascader-node__label {
    font-size: 13px;
  }

  &.in-active-path {
    background-color: #f5f7fa;
  }

  &.is-active {
    background-color: #ecf5ff;
    color: #13d6aa;
  }
}

.year-selector {
  .el-checkbox-group {
    display: flex;
    gap: 15px;
  }
}

.el-form-item {
  margin-bottom: 20px;

  :deep(.el-form-item__label) {
    font-weight: 500;
    color: #666;
  }
}

.el-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* 题型列表优化 - 添加分值配置样式 */
.question-types {
  height: 550px;
  overflow-y: auto;

  .type-item {
    // display: flex;
    // justify-content: space-between;
    // align-items: flex-start;
    padding: 15px 0;
    border-bottom: 1px solid #f0f0f0;
    // transition: all 0.3s;

    &:hover {
      background-color: #f9f9f9;
      // padding-left: 8px;
      // padding-right: 8px;
      border-radius: 4px;
    }

    &:last-child {
      border-bottom: none;
    }

    &.has-count {
      .type-name {
        font-weight: 500;
      }
    }

    .type-info {
      display: flex;
      // flex-direction: column;
      align-items: center;
      justify-content: space-between;
      flex: 1;

      .type-name {
        font-size: 14px;
        color: #333;
        margin-bottom: 4px;
      }

      .type-total {
        font-size: 12px;
        color: #909399;

        &.zero {
          color: #f56c6c;
        }
      }
    }

    .type-config {
      display: flex;
      gap: 10px;

      .count-config,
      .score-config {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100px;

        .config-label {
          font-size: 12px;
          color: #909399;
          margin-bottom: 4px;
        }

        .el-input-number {
          width: 100%;

          :deep(.el-input-number__decrease),
          :deep(.el-input-number__increase) {
            background-color: #f5f7fa;
          }

          &.is-disabled {
            :deep(.el-input__inner) {
              background-color: #f5f7fa;
              color: #c0c4cc;
            }
          }
        }
      }
    }
  }

  /* 总分统计 */
  .total-score-summary {
    margin-top: 15px;
    padding: 10px 0;
    background-color: #f9f9f9;
    border-radius: 4px;

    .total-score,
    .total-questions {
      display: flex;
      justify-content: space-between;
      padding: 5px 15px;
      font-size: 14px;

      .score-value {
        color: #13d6aa;
        font-weight: 600;
        font-size: 16px;
      }

      .questions-value {
        color: #409eff;
        font-weight: 600;
        font-size: 16px;
      }
    }
  }
}

/* 头部操作按钮 */
.header-actions {
  display: flex;
  gap: 10px;

  .el-button {
    font-size: 13px;
  }
}

/* 小型加载状态 */
.loading-state.small {
  padding: 20px 0;
  flex-direction: row;
  gap: 10px;

  .el-icon {
    font-size: 18px;
    margin-bottom: 0;
  }

  span {
    font-size: 14px;
  }
}

/* 配置汇总 */
.config-summary {
  margin-top: 20px;
  animation: slideIn 0.3s ease;

  .summary-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 10px;
  }

  .summary-content {
    background: #f5f7fa;
    padding: 10px;
    border-radius: 4px;
    font-size: 12px;
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-all;
    border: 1px solid #e4e7ed;
  }
}

/* 底部操作 */
.footer-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.03);

  .el-button {
    min-width: 120px;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
}

/* 总题数提示 */
.total-questions-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  span {
    color: #409eff;
    font-weight: 500;

    &.warning {
      color: #e6a23c;
    }
  }
}

.limit-warning {
  color: #f56c6c;
  font-size: 12px;
  background-color: #fef0f0;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 总分统计中的警告文字 */
.total-questions {
  .questions-value {
    &.warning-text {
      color: #f56c6c;
    }
  }

  .total-limit {
    color: #909399;
    font-size: 14px;
  }
}

/* 缺省状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #909399;

  &.small {
    padding: 20px;
  }

  .el-empty {
    --el-empty-padding: 20px;
  }
}

/* 在树形容器中显示空状态时的样式 */
.tree-container {
  min-height: 200px;

  .empty-state {
    height: 100%;
    min-height: 200px;
  }
}

/* 动画 */
// @keyframes slideIn {
//   from {
//     opacity: 0;
//     transform: translateY(10px);
//   }

//   to {
//     opacity: 1;
//     transform: translateY(0);
//   }
// }

/* 响应式调整 */
@media (max-width: 1400px) {
  .paper-generation-container {
    width: 100%;
  }

  .main-content {
    flex-direction: column;
  }
}
</style>
